import { api } from "./api";
import type { AdminBlock, AdminLesson, AdminSection, BlockItem } from "./types";

// Admin CRUD-обёртки. Все запросы идут через тот же `api` helper — он навешивает
// `Authorization: tma <initData>` и парсит ошибки. Бэк требует is_admin=true,
// иначе 401/403 — обрабатываем в UI как обычные ошибки.

// --- Sections --------------------------------------------------------------

export type SectionCreate = {
  id: string;
  title: string;
  available: boolean;
  available_reason: string | null;
  max_per_day: number;
  display_order: number;
};

export type SectionUpdate = Partial<Omit<SectionCreate, "id">>;

export const adminSections = {
  list: () => api<AdminSection[]>("/admin/sections"),
  create: (body: SectionCreate) =>
    api<AdminSection>("/admin/sections", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: string, body: SectionUpdate) =>
    api<AdminSection>(`/admin/sections/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    api<void>(`/admin/sections/${id}`, { method: "DELETE" }),
  reorder: (order: string[]) =>
    api<AdminSection[]>("/admin/sections/reorder", {
      method: "POST",
      body: JSON.stringify({ order }),
    }),
};

// --- Lessons ---------------------------------------------------------------

export type LessonCreate = {
  title: string;
  display_order: number;
};

export type LessonUpdate = Partial<LessonCreate>;

export const adminLessons = {
  list: (sectionId: string) =>
    api<AdminLesson[]>(`/admin/sections/${sectionId}/lessons`),
  create: (sectionId: string, body: LessonCreate) =>
    api<AdminLesson>(`/admin/sections/${sectionId}/lessons`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (lessonId: string, body: LessonUpdate) =>
    api<AdminLesson>(`/admin/lessons/${lessonId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (lessonId: string) =>
    api<void>(`/admin/lessons/${lessonId}`, { method: "DELETE" }),
  reorder: (sectionId: string, order: string[]) =>
    api<AdminLesson[]>(`/admin/sections/${sectionId}/lessons/reorder`, {
      method: "POST",
      body: JSON.stringify({ order }),
    }),
};

// --- Blocks ----------------------------------------------------------------

export type BlockCreate = {
  title: string;
  button_caption: string;
  display_order: number;
  items: BlockItem[];
  dz_required: boolean;
};

export type BlockUpdate = Partial<BlockCreate>;

// --- Upload (аудио/видео от админа) ---------------------------------------

// width/height — только для видео. duration_seconds — для аудио и видео.
// Все опциональные: если ffprobe на сервере упал или поток не определился — поля null.
export type UploadOut = {
  url: string;
  key: string;
  width?: number | null;
  height?: number | null;
  duration_seconds?: number | null;
  is_gif?: boolean;
};

export async function uploadAdminAudio(file: File): Promise<UploadOut> {
  const form = new FormData();
  form.append("file", file);
  // api() сам поставит Authorization; Content-Type для multipart выставит браузер.
  return api<UploadOut>("/admin/upload", {
    method: "POST",
    body: form,
  });
}

export const adminBlocks = {
  list: (lessonId: string) =>
    api<AdminBlock[]>(`/admin/lessons/${lessonId}/blocks`),
  create: (lessonId: string, body: BlockCreate) =>
    api<AdminBlock>(`/admin/lessons/${lessonId}/blocks`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (blockId: string, body: BlockUpdate) =>
    api<AdminBlock>(`/admin/blocks/${blockId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (blockId: string) =>
    api<void>(`/admin/blocks/${blockId}`, { method: "DELETE" }),
  reorder: (lessonId: string, order: string[]) =>
    api<AdminBlock[]>(`/admin/lessons/${lessonId}/blocks/reorder`, {
      method: "POST",
      body: JSON.stringify({ order }),
    }),
};
